/**
 * NotebookShell — the workspace notebook frame.
 *
 * Layout: [ Page body (header + editor) | PageMeta ]
 *
 * The page tree lives in the main workspace sidebar (FRIDAY-69), so the
 * notebook no longer renders a column of its own. On mobile the tree is
 * reached through the sidebar drawer, like boards.
 *
 * The Page details sidebar is owned by `PageMeta` and collapses itself
 * with `friday:notebook-meta-collapsed`.
 */

import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'
import { usePage, usePageTree } from '@/features/notebook/pages'
import { findPath } from '@/features/notebook/tree'
import { useNotebookSubscription } from '@/features/notebook/useNotebookSubscription'
import { PageHeader } from './PageHeader'
import { PageEditor } from './PageEditor'
import { PageMeta } from './PageMeta'

export interface NotebookShellProps {
  wsId: string
}

export function NotebookShell({ wsId }: NotebookShellProps) {
  return (
    <div className="flex h-full min-w-0 flex-col">
      <NotebookMain wsId={wsId} />
    </div>
  )
}

function NotebookMain({ wsId }: { wsId: string }) {
  const params = useParams({ strict: false }) as { pageId?: string }
  const pageId = params.pageId
  const pageQ = usePage(wsId, pageId)
  const tree = usePageTree(wsId)
  const navigate = useNavigate()

  useNotebookSubscription({ wsId, openPageId: pageId })

  const ancestors = useMemo(() => {
    if (!pageId) return []
    return findPath(tree.data ?? [], pageId).slice(0, -1) // exclude self
  }, [pageId, tree.data])

  if (!pageId) {
    return (
      <main className="bg-app flex flex-1 items-center justify-center p-8 text-center">
        <div>
          <div className="mb-3 text-5xl">📓</div>
          <h2 className="mb-1 text-lg font-semibold">Pick a page</h2>
          <p className="text-surface-500 text-sm">
            Choose a page under Pages in the sidebar, or create a new one with +.
          </p>
        </div>
      </main>
    )
  }

  if (pageQ.isLoading) {
    return (
      <main className="bg-app text-surface-500 flex flex-1 items-center justify-center text-sm">
        Loading page…
      </main>
    )
  }

  // FRIDAY-34: an old link to a page that moved to another workspace. The
  // page loaded, so the caller can read it there: go to its new home.
  if (pageQ.data && pageQ.data.workspaceId !== wsId) {
    return <MovedPageRedirect wsId={pageQ.data.workspaceId} pageId={pageQ.data.id} />
  }

  if (!pageQ.data) {
    return (
      <main className="bg-app flex flex-1 items-center justify-center p-8 text-center">
        <div>
          <h2 className="mb-1 text-lg font-semibold">Page not found</h2>
          <p className="text-surface-500 mb-3 text-sm">
            It may have been deleted, or you may not have access.
          </p>
          <Link to="/w/$wsId" params={{ wsId }} className="btn-primary inline-block">
            Back to workspace
          </Link>
        </div>
      </main>
    )
  }

  const page = pageQ.data

  return (
    <main className="bg-app flex min-w-0 flex-1 overflow-hidden">
      <div className="flex min-w-0 flex-1 flex-col">
        <PageHeader
          wsId={wsId}
          page={page}
          ancestors={ancestors}
          onOpenParent={(id) => navigate({ to: '/w/$wsId/n/$pageId', params: { wsId, pageId: id } })}
        />
        {/*
          `key={page.id}` forces React to fully unmount the old PageEditor
          (and its ProseMirror DOM) before mounting the new one. Without
          it, `useEditor(..., [page.id, wsId])` recreates the editor in
          place while React is still holding the old DOM tree, which
          races ProseMirror's mutation observer and throws
          "Failed to execute 'insertBefore' on 'Node': The node before
          which the new node is to be inserted is not a child of this
          node." — the whole notebook view crashes and shows the React
          error boundary. The header doesn't need the key because it's
          just text/icons and re-renders cheaply on data change.
        */}
        <div className="min-h-0 flex-1" key={page.id}>
          <PageEditor wsId={wsId} page={page} />
        </div>
      </div>
      <PageMeta wsId={wsId} page={page} />
    </main>
  )
}

function MovedPageRedirect({ wsId, pageId }: { wsId: string; pageId: string }) {
  const navigate = useNavigate()
  useEffect(() => {
    navigate({ to: '/w/$wsId/n/$pageId', params: { wsId, pageId }, replace: true })
  }, [navigate, wsId, pageId])
  return (
    <main className="bg-app text-surface-500 flex flex-1 items-center justify-center text-sm">
      Opening page…
    </main>
  )
}
